'use client';

import clsx from 'clsx';
import { type FC, type ReactNode } from 'react';

import { embededGuideContainerId } from '@/constants/connect-and-engage';
import useHasContent from '@/hooks/useHasContent';

/**
 * Props for {@link PageWrapper}.
 */
interface PageWrapperProps {
  /** Page content rendered inside the main wrapper. */
  children?: ReactNode;
  /** Additional Tailwind classes merged onto the inner content wrapper. */
  className?: string;
}

const embedGuideContainerClass = 'w-full px-0 md:pe-10 md:ps-6 pt-0 md:pt-6';
const pageWrapperClass = 'my-[30px] md:mx-[25px]';

/**
 * Top-level layout wrapper used by most pages.
 *
 * Renders an embedded-guide container above the page content. The container's
 * layout classes are applied only when a third-party guide widget has injected
 * content into it; otherwise the div remains unstyled so it takes no space.
 *
 * @param props - {@link PageWrapperProps}
 */
const PageWrapper: FC<PageWrapperProps> = ({ children, className, ...childrenprops }) => {
  const [hasContent, guideRef] = useHasContent();
  return (
    <div>
      <div
        ref={guideRef}
        className={clsx(hasContent && embedGuideContainerClass)}
        id={embededGuideContainerId}
        data-testid={embededGuideContainerId}
      />
      <div className={clsx(pageWrapperClass, className)} {...childrenprops}>
        {children}
      </div>
    </div>
  );
};

export default PageWrapper;
